import { useState, useEffect } from 'react';
import {
  Alert,
  AlertIcon,
  Avatar,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { ImageUploader } from '../bounties/ImageUploader';
import { useStorage } from '../../hooks/useStorage';
import { useUser } from '../../contexts/UserContext';
import { useAuth } from '../../contexts/AuthContext';
import { useResearcherProfile, saveResearcherProfile, deleteResearcherProfile } from '../../hooks/useResearchers';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { RESEARCH_TYPES } from '../../utils/formatters';
import { LoadingSpinner } from '../common/LoadingSpinner';

export default function CreateResearcherProfile() {
  const { currentUser } = useAuth();
  const { userData, loading: userLoading } = useUser();
  const { uploadFile, uploading, progress } = useStorage();
  const navigate = useNavigate();
  const toast = useToast();

  const { profile: existingProfile, loading: profileLoading } = useResearcherProfile(currentUser?.uid);

  const [tagline, setTagline] = useState('');
  const [bio, setBio] = useState('');
  const [services, setServices] = useState([]);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  // Pre-populate form if editing existing profile
  useEffect(() => {
    if (existingProfile && !prefilled) {
      setTagline(existingProfile.tagline || '');
      setBio(existingProfile.bio || '');
      setServices(existingProfile.services || []);
      setWebsiteUrl(existingProfile.websiteUrl || '');
      setPrefilled(true);
    }
  }, [existingProfile, prefilled]);

  const isEditing = !!existingProfile;

  const toggleService = (key) => {
    setServices((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const isValid =
    bio.trim().length >= 20 &&
    services.length > 0;

  const handleSubmit = async () => {
    if (!currentUser) return;
    setSubmitting(true);

    try {
      // Use context userData if available, otherwise fetch directly
      let user = userData;
      if (!user) {
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        user = snap.exists() ? snap.data() : {};
      }

      let avatarUrl = existingProfile?.avatarUrl || '';

      // Upload new photo if provided
      if (imageFiles.length > 0) {
        const file = imageFiles[0];
        const ext = file.name.split('.').pop();
        const path = `researcherProfiles/${currentUser.uid}/avatar_${Date.now()}.${ext}`;
        avatarUrl = await uploadFile(file, path);
      }

      await saveResearcherProfile(
        currentUser.uid,
        {
          displayName: user.isFullyAnonymous ? 'Anonymous' : (user.anonymizedUsername || 'Researcher'),
          avatarUrl,
          tagline: tagline.trim(),
          bio: bio.trim(),
          services,
          websiteUrl: websiteUrl.trim(),
          reputation: user.reputation || {},
        },
        !isEditing
      );

      toast({
        title: isEditing ? 'Profile updated!' : 'Profile published!',
        description: isEditing
          ? 'Your researcher profile has been updated.'
          : 'Your researcher profile is now live.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      navigate('/researchers');
    } catch (error) {
      toast({
        title: 'Error saving profile',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!currentUser) return;
    setDeleting(true);

    try {
      await deleteResearcherProfile(currentUser.uid);
      toast({
        title: 'Profile removed',
        description: 'Your researcher profile has been unpublished.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
      navigate('/researchers');
    } catch (error) {
      toast({
        title: 'Error removing profile',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setDeleting(false);
    }
  };

  if (profileLoading || userLoading) {
    return <LoadingSpinner message="Loading profile..." />;
  }

  // Block fully anonymous users
  if (userData?.isFullyAnonymous) {
    return (
      <Container maxW="2xl" py={8}>
        <Alert status="warning" rounded="lg">
          <AlertIcon />
          <Box>
            <Text fontWeight="semibold">Anonymous Mode Enabled</Text>
            <Text fontSize="sm" mt={1}>
              You must disable Fully Anonymous Mode in{' '}
              <Button as={RouterLink} to="/settings" variant="link" colorScheme="orange" size="sm">
                Settings
              </Button>{' '}
              before publishing a researcher profile.
            </Text>
          </Box>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="2xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Box>
          <Heading size="lg">{isEditing ? 'Edit Your Profile' : 'Publish Your Profile'}</Heading>
          <Text color="gray.600" mt={1}>
            Advertise your research services to collectors
          </Text>
        </Box>

        <Box bg={cardBg} rounded="xl" p={{ base: 4, md: 8 }} boxShadow="sm" borderWidth={1} borderColor={borderColor}>
          <VStack spacing={6} align="stretch">
            {/* Photo */}
            <FormControl>
              <FormLabel>Profile Photo</FormLabel>
              {isEditing && existingProfile?.avatarUrl && imageFiles.length === 0 && (
                <HStack mb={3}>
                  <Avatar size="lg" src={existingProfile.avatarUrl} name={existingProfile.displayName} />
                  <Text fontSize="sm" color="gray.500">Current photo. Upload a new one to replace it.</Text>
                </HStack>
              )}
              <ImageUploader onFilesChange={setImageFiles} maxFiles={1} />
              {uploading && (
                <Text fontSize="sm" mt={1}>Uploading... {progress}%</Text>
              )}
            </FormControl>

            {/* Tagline */}
            <FormControl>
              <FormLabel>Short Description</FormLabel>
              <Input
                placeholder="e.g. Art historian specializing in 17th century Dutch paintings"
                value={tagline}
                onChange={(e) => setTagline(e.target.value.slice(0, 140))}
                maxLength={140}
              />
              <FormHelperText>{tagline.length}/140 characters</FormHelperText>
            </FormControl>

            {/* Bio */}
            <FormControl isRequired>
              <FormLabel>About You</FormLabel>
              <Textarea
                placeholder="Tell potential clients about your background, expertise, and research approach..."
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={5}
                resize="vertical"
              />
              <FormHelperText>{bio.length} characters (min 20)</FormHelperText>
            </FormControl>

            {/* Services */}
            <FormControl isRequired>
              <FormLabel>Services Offered</FormLabel>
              <FormHelperText mb={3}>Select the types of research you offer</FormHelperText>
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                {Object.entries(RESEARCH_TYPES).map(([key, { label, color }]) => (
                  <Checkbox
                    key={key}
                    isChecked={services.includes(key)}
                    onChange={() => toggleService(key)}
                    colorScheme={color}
                  >
                    {label}
                  </Checkbox>
                ))}
              </SimpleGrid>
            </FormControl>

            {/* Website */}
            <FormControl>
              <FormLabel>Website</FormLabel>
              <Input
                placeholder="https://yourwebsite.com"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                type="url"
              />
              <FormHelperText>Optional</FormHelperText>
            </FormControl>


          </VStack>
        </Box>

        {/* Actions */}
        <HStack justify={isEditing ? 'space-between' : 'flex-end'}>
          {isEditing && (
            <Button
              onClick={handleDelete}
              isLoading={deleting}
              loadingText="Removing..."
              colorScheme="red"
              variant="ghost"
              size="lg"
            >
              Remove Profile
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            isDisabled={!isValid}
            isLoading={submitting || uploading}
            loadingText={uploading ? `Uploading... ${progress}%` : 'Saving...'}
            colorScheme="orange"
            size="lg"
          >
            {isEditing ? 'Update Profile' : 'Publish Profile'}
          </Button>
        </HStack>
      </VStack>
    </Container>
  );
}
